import React from 'react'
import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import closeRedIcon from 'assets/icons/close-red.svg'
import lockClosedIcon from 'assets/icons/lock-closed-blue.svg'
import lockOpenIcon from 'assets/icons/lock-open-blu.svg'
import tickGreenIcon from 'assets/icons/tick-green.svg'
import { DATE_FORMATS } from 'constants/dateFormats'
import { ApprovalDeal } from 'ts/interfaces/approvalDeal.interface'
import { getDateFormatter } from '../HelperFns'

export const exportDealFullDateUTCFormater = getDateFormatter(DATE_FORMATS.DISPLAY_WITH_TIME, false)

const renderStatusWithIcon = (value: unknown, errorLabel: string) => {
  if (value == null) {
    return null
  }

  let statusText: string | null = null
  if (typeof value === 'string') {
    statusText = value
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    statusText = String(value)
  }

  if (!statusText) {
    return null
  }
  const iconSrc = statusText === errorLabel ? closeRedIcon : tickGreenIcon
  return React.createElement(
    'span',
    { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
    React.createElement('img', { src: iconSrc, alt: statusText, width: 14, height: 14 }),
    React.createElement('span', null, statusText)
  )
}

const renderYesNo = (value: unknown) => {
  if (value !== 'Y' && value !== 'N') {
    return null
  }
  const isYes = value === 'Y'
  return React.createElement(
    'span',
    { style: { color: isYes ? '#d6002a' : '#00b04f' } },
    isYes ? 'Yes' : 'No'
  )
}

export const approvalColDef: ColDef[] = [
  // ===== DEFAULT VISIBLE COLUMNS =====
  {
    headerName: 'Time Since Submit',
    field: 'timeSinceSubmit',
    width: 150,
  },
  {
    headerName: 'Country Name',
    field: 'countryName',
    width: 150,
  },
  {
    headerName: 'Customer Name',
    field: 'customerName',
    width: 150,
  },
  {
    headerName: 'Deal No',
    field: 'dealNr',
    width: 150,
  },
  {
    headerName: 'MC Code',
    field: 'mcCode',
    width: 150,
  },
  {
    headerName: 'Est. Deal Size (Net)',
    field: 'estDealSizeNet',
    width: 150,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
  },
  {
    headerName: 'Sales Rep',
    field: 'salesRep',
    width: 150,
  },
  {
    headerName: 'Customer Segment',
    field: 'customerSegment',
    width: 150,
  },
  {
    headerName: 'Bus Model',
    field: 'busModel',
    width: 150,
  },
  {
    headerName: 'Currency',
    field: 'currency',
    width: 150,
  },
  {
    headerName: 'Deal Begin Date',
    field: 'dealBeginDate',
    colId: 'dealBeginDate',
    width: 200,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Deal End Date',
    field: 'dealEndDate',
    colId: 'dealEndDate',
    width: 200,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Deal Description',
    field: 'dealDescription',
    width: 150,
  },
  {
    headerName: 'Completed Group',
    field: 'completedGroup',
    width: 150,
  },
  {
    headerName: 'User Decision',
    field: 'userDecision',
    width: 150,
  },
  {
    headerName: 'Pending Level',
    field: 'pendingLevel',
    width: 150,
  },

  // ===== HIDDEN BY DEFAULT =====
  {
    headerName: 'Submit Date',
    field: 'submitDate',
    colId: 'submitDate',
    width: 150,
    hide: true,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Version #',
    field: 'dealVersion',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Country Code',
    field: 'countryCode',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Engagement Model',
    field: 'engagementModel',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Deal Type Mapping',
    field: 'dealTypeMapping',
    width: 150,
    hide: true,
  },
  {
    headerName: 'External Notes',
    field: 'externalNotes',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Sent By',
    field: 'sentBy',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Auth Status',
    field: 'authStatus',
    width: 150,
    hide: true,
    cellRenderer: ({ value }: ValueFormatterParams<ApprovalDeal>) =>
      renderStatusWithIcon(value, 'Not Authorized'),
  },
  {
    headerName: 'Approval Status',
    field: 'approvalStatus',
    width: 150,
    hide: true,
    cellRenderer: ({ value }: ValueFormatterParams<ApprovalDeal>) =>
      renderStatusWithIcon(value, 'Not Approved'),
  },
  {
    headerName: 'Lock Flag',
    field: 'lockFlag',
    width: 150,
    hide: true,
    cellRenderer: ({ value }: ValueFormatterParams<ApprovalDeal>) => {
      if (value !== 'Y' && value !== 'N') {
        return null
      }
      const isLocked = value === 'Y'
      const iconSrc = isLocked ? lockClosedIcon : lockOpenIcon
      const altText = isLocked ? 'Locked' : 'Unlocked'
      return React.createElement(
        'span',
        { style: { display: 'flex', alignItems: 'center', justifyContent: 'center' } },
        React.createElement('img', { src: iconSrc, alt: altText, width: 16, height: 16 })
      )
    },
  },
  {
    headerName: 'Deal Locked for (hh:mm)',
    field: 'dealLockedFor',
    width: 150,
    hide: true,
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Lock User',
    field: 'lockUser',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Last Message',
    field: 'lastMessage',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Status Update Type',
    field: 'statusUpdateType',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Status Update Time',
    field: 'msgTime',
    width: 150,
    hide: true,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Quote Status',
    field: 'quoteStatus',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Lead Bus. Group',
    field: 'leadBusGroup',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Business Unit',
    field: 'businessUnit',
    width: 150,
    hide: true,
  },
  {
    headerName: 'High Risk',
    field: 'highRisk',
    width: 150,
    hide: true,
    cellRenderer: ({ value }: ValueFormatterParams<ApprovalDeal>) => renderYesNo(value),
  },
  {
    headerName: 'Win/Loss Status',
    field: 'winLossStatus',
    width: 150,
    hide: true,
  },
  {
    headerName: 'High risk deal EUV Not Completed Yet',
    field: 'highRiskDealEUVNotCompletedYet',
    width: 150,
    hide: true,
    cellRenderer: ({ value }: ValueFormatterParams<ApprovalDeal>) => renderYesNo(value),
  },
  {
    headerName: 'Deal Create Date',
    field: 'createDate',
    colId: 'createDate',
    width: 150,
    hide: true,
    cellClass: 'ag-right-aligned-cell',
    headerClass: 'ag-right-aligned-cell',
    filter: 'customDateFilter',
    valueFormatter: (params: ValueFormatterParams<ApprovalDeal>) =>
      exportDealFullDateUTCFormater(params.value),
  },
  {
    headerName: 'Dist. Manager Name',
    field: 'distManagerName',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Deal Lead',
    field: 'dealLead',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Opportunity ID',
    field: 'opportunityId',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Deal Source',
    field: 'dealSource',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Notes',
    field: 'notes',
    width: 150,
    hide: true,
  },
  {
    headerName: 'State/Province',
    field: 'stateProvince',
    width: 150,
    hide: true,
  },
  {
    headerName: 'Tenant ID',
    field: 'tenantId',
    width: 150,
    hide: true,
  },
]
